'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Send, Loader2 } from 'lucide-react'

export interface ChatInputProps {
  onSend: (content: string) => void
  disabled?: boolean
  placeholder?: string
  className?: string
}

/**
 * Chat input component with textarea and send button
 * Supports Enter to send, Shift+Enter for newline
 */
export function ChatInput({
  onSend,
  disabled = false,
  placeholder = 'Type a message...',
  className,
}: ChatInputProps) {
  const [value, setValue] = React.useState('')
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  // Auto-resize textarea based on content
  const adjustHeight = React.useCallback(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }
  }, [])

  React.useEffect(() => {
    adjustHeight()
  }, [value, adjustHeight])

  const handleSubmit = React.useCallback(() => {
    const trimmedValue = value.trim()
    if (trimmedValue && !disabled) {
      onSend(trimmedValue)
      setValue('')
      // Reset height after clearing
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }, [value, disabled, onSend])

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter without shift sends the message
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  return (
    <div
      className={cn(
        'flex items-end gap-2 rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900',
        className
      )}
    >
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        className={cn(
          'min-h-[40px] flex-1 resize-none border-0 bg-transparent p-2 focus-visible:ring-0 focus-visible:ring-offset-0',
          'placeholder:text-gray-400 dark:placeholder:text-gray-500'
        )}
      />
      <Button
        type="button"
        size="icon"
        onClick={handleSubmit}
        disabled={disabled || !value.trim()}
        className="h-10 w-10 shrink-0"
      >
        {disabled ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Send className="h-4 w-4" />
        )}
        <span className="sr-only">Send message</span>
      </Button>
    </div>
  )
}

/**
 * Keyboard shortcuts hint component
 */
export function ChatInputHint({ className }: { className?: string }) {
  return (
    <p className={cn('text-xs text-gray-400 dark:text-gray-500', className)}>
      Press <kbd className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs dark:bg-gray-800">Enter</kbd> to send,{' '}
      <kbd className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs dark:bg-gray-800">Shift + Enter</kbd> for new line
    </p>
  )
}
